import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Check from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import CardHeader from '@mui/material/CardHeader';
import { useAuth0 } from '@auth0/auth0-react';
import { useApolloClient } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFlag } from '@openfeature/react-sdk';
import { LAST_ORG_USED_KEY } from '../../../utils/ApiConstants';
import { UserContext } from '../../../App';
import { USER_ORGS } from '../../../graphql/queries/user';
import useNRLazyQuery from '../../../graphql/useNRLazyQuery';
import NRTagList from '../../../components/NRTagList/NRTagList';
import useCurrentUserAccess from '../../../hooks/useCurrentUserAccess';
import { mapAccessTagsToNrTags } from '../../../hooks/accessTagIcons';
import { FEATURE_FLAGS } from '../../../constants/constants';
import { drawerWidth } from '../NRMenu';

function NROrgSelector() {
  const theme = useTheme();
  const { value: isUsersAndAccessSettingsEnabled } = useFlag(FEATURE_FLAGS.SETTINGS_USERS_AND_ACCESS, false);
  const { currentOrg, setIdToken, setCurrentOrg, currentOrgId, setCurrentOrgId, roles, setRoles, email, setEmail, setIsChangingOrg, idToken } =
    useContext(UserContext);
  const { displayTags } = useCurrentUserAccess();
  const nrDisplayTags = useMemo(() => {
    if (isUsersAndAccessSettingsEnabled) {
      return mapAccessTagsToNrTags(displayTags, theme.palette.text.secondary, 14);
    }

    // REMOVE(settings-users-and-access): Auth0 roles → tags; use RBAC displayTags only when flag is retired.
    return roles?.map(role => ({ name: role })) ?? [];
  }, [isUsersAndAccessSettingsEnabled, displayTags, roles, theme.palette.text.secondary]);
  const auth0Namespace = process.env.REACT_APP_AUTH0_NAMESPACE;
  const permissionsNamespace = auth0Namespace + 'permissions';
  const rolesNamespace = auth0Namespace + 'roles';
  const currentOrgNamespace = auth0Namespace + 'org_name';
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [disableDropdown, setDisableDropdown] = useState(true);
  const [orgs, setOrgs] = useState(null);
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const [, , , getUserOrgs] = useNRLazyQuery({
    query: USER_ORGS,
    qlObjKey: 'userOrgs',
    options: { context: { version: 'v3' } },
    transformData: userOrgs => {
      const sortedOrgs = [...userOrgs].sort((a, b) => (a?.DisplayName || '').localeCompare(b?.DisplayName || '', undefined, { sensitivity: 'base' }));
      setOrgs(sortedOrgs);
      setDisableDropdown(sortedOrgs.length < 2);
    }
  });

  useEffect(() => {
    if (idToken && idToken[permissionsNamespace]?.length) {
      getUserOrgs();
    }
  }, [idToken]);

  async function handleOnChange(value) {
    if (!value) return;
    try {
      setIsChangingOrg(true);
      let idToken = await getAccessTokenSilently({
        ignoreCache: true,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        organization: value
      });

      if (!!idToken) {
        idToken = await getIdTokenClaims({
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        });
        setIdToken(idToken);

        const lastOrgUsedId = localStorage.getItem(idToken.email + LAST_ORG_USED_KEY);

        if (!!idToken.org_id && idToken.org_id !== lastOrgUsedId) {
          localStorage.setItem(idToken.email + LAST_ORG_USED_KEY, idToken.org_id);
          setCurrentOrg(idToken[currentOrgNamespace]);
          setCurrentOrgId(idToken.org_id);
        }

        setEmail(idToken?.email);

        // REMOVE(settings-users-and-access): Refresh Auth0 roles on org switch; drop when flag is retired.
        if (idToken[rolesNamespace]) {
          setRoles(idToken[rolesNamespace]);
        }

        client.clearStore();
        navigate(pathname === '/' ? 0 : '/');
      }
      handleClose();
    } finally {
      setIsChangingOrg(false);
    }
  }

  /**
   * Menu handling
   */
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: `calc(${drawerWidth}px + ${theme.spacing(1)})` }}>
      {!!orgs?.length && (
        <>
          <Button
            id="org-selector-button"
            aria-controls={open ? 'org-selector-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            variant="text"
            disableElevation
            color="inherit"
            onClick={!disableDropdown ? handleClick : undefined}
            endIcon={!disableDropdown && <KeyboardArrowDown />}
            sx={{
              textTransform: 'unset',
              ...theme.typography.subtitle2,
              padding: theme.spacing(1),
              maxHeight: theme.spacing(4.5),
              ...(!disableDropdown && { paddingRight: theme.spacing(1.5) }),
              '&.Mui-disabled': {
                color: theme.palette.text.primary,
                backgroundColor: 'unset'
              },
              '& .MuiButton-endIcon': {
                color: theme.palette.text.icon,
                '&>*:nth-of-type(1)': {
                  fontSize: theme.spacing(3)
                }
              }
            }}
            disabled={disableDropdown}
          >
            {currentOrg}
          </Button>
          <Menu
            id="org-selector-menu"
            aria-labelledby="org-selector-button"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            slotProps={{ paper: { sx: { minWidth: theme.spacing(36.25) } } }}
          >
            <CardHeader
              titleTypographyProps={{ sx: { ...theme.typography.body1 } }}
              subheaderTypographyProps={{ component: 'div' }}
              title={email}
              subheader={nrDisplayTags.length ? <NRTagList tags={nrDisplayTags} maxWidth={theme.spacing(36.25)} /> : null}
              sx={{
                position: 'sticky',
                marginTop: theme.spacing(-1),
                top: 0,
                zIndex: 1,
                backgroundColor: theme.palette.background.paper,
                ...theme.palette.elevation
              }}
            />
            <Divider sx={{ position: 'sticky', top: theme.spacing(9.485), marginBottom: theme.spacing(0.5) }} />
            {!!orgs &&
              orgs.map(({ Id, DisplayName }) => (
                <MenuItem
                  selected={currentOrgId === Id}
                  sx={{ justifyContent: 'space-between', padding: theme.spacing(1.5, 2) }}
                  onClick={() => currentOrgId !== Id && handleOnChange(Id)}
                  key={Id}
                >
                  {DisplayName}
                  {currentOrgId === Id && <Check sx={{ marginLeft: theme.spacing(1) }} />}
                </MenuItem>
              ))}
          </Menu>
        </>
      )}
    </Box>
  );
}
export default NROrgSelector;
